export enum CouponType {
  STANDARD = 'standard',
  LOYALTY = 'loyalty',
  METERED = 'metered',
}

export enum OfferNetworkType {
  DOSH = 'DOSH',
  VENMO = 'VENMO',
}

export enum OfferScopeType {
  VENUE = 'VENUE',
  MARKETPLACE = 'MARKETPLACE', // makes offer valid across all marketplace regardless of venue, so any transaction qualifies, i.e. mcc code offers
}

export enum RedemptionType {
  LINKED_CARD = 'LINKED_CARD',
  QR_CODE = 'QR_CODE',
}

export enum SegmentType {
  BASE = 'BASE',
  MUG = 'MUG',
  TARGETED = 'TARGETED',
}

export enum SegmentActionType {
  PULSE = 'PULSE',
  REPLACE = 'REPLACE',
  TERMINATE = 'TERMINATE',
  MODIFY = 'MODIFY',
}

export type SegmentAction = {
  segmentUuid: string | null;
  action: SegmentActionType | null;
};

export enum PromotionType {
  STANDARD = 'STANDARD',
  CASH_FLASH = 'CASH_FLASH',
}

export enum TargetingRuleType {
  BASE = 'BASE',
  MUG = 'MUG',
  MARKETPLACE = 'MARKETPLACE', // this rule type has be deprecated in favor of segment.marketplace
  AUDIENCE = 'AUDIENCE',
  OFFER_THROTTLE_V1 = 'OFFER_THROTTLE_V1',
  INVERSE_OFFER_THROTTLE_V1 = 'INVERSE_OFFER_THROTTLE_V1',
}

export enum TermType {
  CAMPAIGN_FEE = 'CAMPAIGN_FEE',
  MARKETING_FEE = 'MARKETING_FEE',
  CASHBACK = 'CASHBACK',
}

export enum TermUnits {
  PERCENTAGE = 'PERCENTAGE',
  USD = 'USD',
}

export enum PayerType {
  ADVERTISER = 'ADVERTISER',
  MARKETPLACE = 'MARKETPLACE',
}

export enum AdvertiserType {
  MERCHANT = 'MERCHANT',
  MARKETPLACE = 'MARKETPLACE',
  OFFER_NETWORK = 'OFFER_NETWORK',
  AGENCY = 'AGENCY',
}

export enum BudgetType {
  FIXED_AMOUNT = 'FIXED_AMOUNT',
  OPEN = 'OPEN',
  FIXED_AMOUNT_FIXED_DATE_RANGE = 'FIXED_AMOUNT_FIXED_DATE_RANGE',
}

export type MatchParams = {
  merchantId: string;
  merchantName: string;
};

export type OfferSummary = {
  merchantName: string;
  merchantUuid: string;
  offerUuid: string;
  offerDescription: string;
  offerName: string;
  offerStartDate: string;
  offerEndDate: string;
  offerScopeType: string;
  offerVisible: boolean;
  offerDraft: boolean;
  offerDeleted: boolean;
  offerKeywords: string;
  offerStatus: OfferStatus;
  offerSource: string;
  offerSchedule?: OfferSchedule[];
  campaignId: string;
  couponId: string;
  addDiscoveredVenuesDynamically: boolean;
  legacyVenmoFeatured: boolean;
  offerRedemptionType: string;
  offerPriority: number;
  merchantServiceCampaignUuid: string;
  couponUuid: string;
  couponType: CouponType;
  paymentNetworkMCC: string;
  paymentNetworkMCCDescription: string;
  segments: Segment[];
  // Exact Spend used for Loyalty Coupon Offers. Stringyfied Array of Float
  exactSpendToActivate?: string;
  minimumSpendToActivate?: string;
};

export type Segment = {
  uuid: string;
  financialTermUuid: string;
  financialTerm: FinancialTerm;
  baseCashbackAmount?: number | string;
  baseCashbackPercentage?: number | string;
  cashbackAmount: number | string;
  cashbackDailyLimit: number | string;
  cashbackPercentage: number | string;
  minSpendToActivate: number;
  exactSpendToActivate: number;
  segmentType: SegmentType;
  segmentPriority: number;
  startDate: string;
  endDate?: string;
  deleted: boolean;
  offerTitleOverride: string;
  hideOfferForSegment: boolean;
  targetingRules: TargetingRule[];
  marketplaces: string[];
  promotionType: PromotionType;
  throttle?: Throttle;
  audience?: Audience;
};

export type FinancialTerm = {
  uuid: string;
  createdAt: string;
  financialTermItems: FinancialTermItem[];
};

export type FinancialTermItem = {
  id: number;
  uuid: string;
  termType: TermType;
  payerType: PayerType;
  termValue: number;
  termUnits: TermUnits;
  termUnitsScaleFactor: number;
  campaignId: string;
  createdAt: string;
  insertionOrderTicket: string;
  budgetId: string;
};

export type FinancialTermItemInput = {
  termType: TermType;
  payerType: PayerType;
  termValue: number;
  termUnits: TermUnits;
};

export type DataByKey = {
  [key: string]: any;
};

export type TargetingRule = {
  type: TargetingRuleType;
  data: string[];
  dataByKey: DataByKey;
};

export type Throttle = {
  uuid: string;
  min: number;
  max: number;
  name: string;
  description: string;
  updatedAt: Date;
  adjustmentReason: string;
  adjustmentReasonDetail: string;
  adjustmentTicketUrl: string;
  adjustmentUpdatedBy: string;
  governorRanges: GovernorRanges;
};

export type GovernorRanges = {
  includeRange: Range;
  excludeRange: Range;
  includeRangeEnabled: boolean;
  excludeRangeEnabled: boolean;
};

export type Range = {
  min: number;
  max: number;
};

export type Audience = {
  externalId: string;
  name: string;
  status: string;
  effectiveAt: string;
  audienceUuid: string;
  subscriber: string;
};

export type PulseSegmentResponse = {
  offer: OfferSummary;
};

export type PulseFormData = {
  offerUuid: string;
  segmentUuid: string;
  segmentType: string;
  audience: string;
  pulseStartDt: Date;
  pulseStartTm: string;
  pulseEndDt: Date | null;
  pulseEndTm: string;
  cashbackAmount: string | number | null;
  cashbackPercentage: string | number | null;
  cashbackDailyLimit: string | number;
  baseCashbackAmount?: string | number;
  baseCashbackPercentage?: string | number;
  promotionType: PromotionType;
  marketingFee: string;
  insertionOrderRef: string;
  marketplaces: string[];
};

export type AddSegmentResponse = {
  createOfferSegment: {
    merchantName: string;
    merchantUuid: string;
    offerUuid: string;
    segments: Segment[];
  };
};

export type ReplaceSegmentFormData = {
  offerUuid: string;
  segmentUuid: string;
  segmentType: string;
  replaceDate: Date;
  replaceTime: string;
  endDate: Date;
  endTime: string;
  cashbackDailyLimit: string | number;
  marketingFee: string;
  insertionOrderRef: string;
  marketplaces: string[];
  financialTerm: FinancialTerm;
  targetingRules: TargetingRule[];
  ongoingEndDate: boolean;
  financialTermUnit: TermUnits;
  cashback: string | number;
  audience: string;
};

export type AddSegmentFormData = {
  mugId: string | number;
  offerUuid: string;
  insertionOrderRef: string;
  segmentPriority: string | number;
  startDate: Date;
  startTime: string;
  endDate: Date;
  endTime: string;
  offerTitleOverride: string;
  hideOfferForSegment: boolean;
  cashbackDailyLimit: string | number;
  baseCashback?: string | number;
  marketingFee: string;
  marketplaces: string[];
  financialTerm: FinancialTerm;
  targetingRules: TargetingRule[];
  ongoingEndDate: boolean;
  useThrottle: string | null;
  financialTermUnit: TermUnits;
  cashback: string | number;
  max: number;
  min: number;
};

export type Budget = {
  budgetName: string;
  budgetAmount: {
    value: number;
    currency: string;
  };
  approvalDate: Date | string;
  budgetStartDate: Date | string;
  budgetEndDate: Date | string;
  advertiser?: {
    name: string;
  };
  uuid: string;
  type: BudgetType;
};

export type CampaignFormData = {
  advertiserMerchantUuid: string;
  advertiserName: string;
  advertiserType: AdvertiserType;
  campaignStartDt: Date;
  campaignStartTm: string;
  campaignEndDt: Date;
  campaignEndTm: string;
  budgetName: string;
  budgetType: BudgetType;
  budgetAmount: number;
  budgetAdvertiserType: AdvertiserType;
  approvalDate: Date;
  campaignName: string;
  insertionOrderRef: string;
  budgets: Budget[];
};

export enum AdjustmentReason {
  OVERPACING = 'Overpacing',
  UNDERPACING = 'Underpacing',
  OTHER = 'Other',
}

export type ThrottleFormData = {
  uuid: string;
  throttleRange: string | number;
  name: string;
  adjustmentReason: AdjustmentReason;
  adjustmentReasonDetail?: string;
  adjustmentTicketUrl: string;
  governorIncludeMin: number;
  governorIncludeMax: number;
  governorIncludeEnabled: boolean;
  governorExcludeMin: number;
  governorExcludeMax: number;
  governorExcludeEnabled: boolean;
  description: string;
  min: number;
  max: number;
};

export type UpdateThrottleResponse = {
  throttle: Throttle;
};

export type SetOfferPropertiesResponse = {
  offer: OfferSummary;
};

export type SetOfferLimitsResponse = {
  success: boolean;
  response: any;
};

export type SetOfferLimitsFormData = {
  offerUuid: string;
  cashbackPercentageLimit: string | null;
  minimumSpendToActivate: string | null;
};

export type SetOfferPropertiesFormData = {
  offerUuid: string;
  startDate: Date;
  startTime: string;
  endDate: Date;
  endTime: string;
  offerDescription: string;
  keywords: string;
  merchantCategoryCodes: string[];
  merchantCategoryCodesToRemove: string[];
  updatedBy: string;
};

export type OfferSchedule = {
  endDate?: string;
  startDate: string;
};

export enum OfferStatus {
  Completed = 0,
  Active,
  Future,
}

export type Offer = {
  offerName: string;
  offerSource: string;
  offerStartDate: Date;
  offerEndDate: Date;
  offerSchedule?: OfferSchedule[];
  offerStatus: OfferStatus;
  offerDescription: string;
  offerVisible: boolean;
  offerDraft: boolean;
  offerDeleted: boolean;
  couponId: string;
  campaignId: string;
  offerUuid: string;
  offerKeywords: string;
  segments: Segment[];
  paymentNetworkMCC: string;
  paymentNetworkMCCDescription: string;
};

export type QueryResponse = {
  offerFindBy: {
    offers: Offer[];
  };
};

export type Advertiser = {
  name: string;
  type: AdvertiserType;
};

export type Campaign = {
  uuid: string;
  name: string;
  startDate: string;
  endDate: string;
  createdAt: string;
};

export enum VenueType {
  ONLINE = 'online',
  NEARBY = 'nearby',
  ALL = 'all',
}

export enum SpecialVenueType {
  BRAND_VENUE = 'BRAND_VENUE',
  NO_LOCATIONS = 'NO_LOCATIONS',
}
